import {
  Resources,
  TemplateAdapter,
  TransactionAdapter,
  TransactionStatus,
  TransactionStatusType,
  WorkflowStatus,
  WorkflowSubmitDatasetTaskMetadata,
  WorkflowTaskAdapter,
  WorkflowTaskStatus,
  WorkflowTaskType,
} from "@validereinc/domain";
import classNames from "classnames/bind";
import styles from "./WorkflowTaskSubmitDataset.module.scss";
import {
  Banner,
  Button,
  FileInput,
  Form,
  KeyValueList,
  NumberDataDisplay,
  TextDataDisplay,
  useForm,
  useToast,
} from "@validereinc/common-components";
import React, { useMemo, useRef, useState } from "react";
import mime from "mime";
import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthenticatedContext } from "#src/contexts/AuthenticatedContext.helpers";
import { useListDatasets } from "#src/components/hooks/adapters/useDatasets";
import { snakeCase } from "es-toolkit/compat";
import { downloadBlob } from "@validereinc/utilities";
import { ExceptionUtils } from "#src/utils/exception";
import {
  uploadFileForDatasetImport,
  type DatasetFileImportRegistrationType,
} from "#src/utils/datasetFileImport";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { DataSetSubmissionDetailRoutePath } from "#src/routes/data-tools/submissions/datasets/[transactionId]/detail";

type WorkflowSubmitDatasetProps = {
  workflowTask: WorkflowTaskType;
  datasetId?: string;
};

const acceptedMimeTypes = [
  mime.getType("csv"),
  mime.getType("xls"),
  mime.getType("xlsx"),
  mime.getType("json"),
].join(",");

const SubmitDatasetTaskFormSchema = z.object({
  dataset: z.string().uuid(),
  files: z
    .array(z.instanceof(File))
    .refine((files) => files.every((f) => acceptedMimeTypes.includes(f.type)), {
      message: "Invalid file type",
    }),
});
type SubmitDatasetTaskFormType = z.infer<typeof SubmitDatasetTaskFormSchema>;

const getBannerDetails = (
  transactionId?: string,
  transactionStatus?: TransactionStatusType,
  uploadError?: boolean
): {
  titleText: string;
  description: string;
  variant: "success" | "error" | "warning" | "loading";
  action: React.ReactElement | null;
} | null => {
  if (transactionStatus === TransactionStatus.COMPLETED) {
    return {
      titleText: "Successfully uploaded data",
      description: "",
      variant: "success",
      action: null,
    };
  } else if (transactionStatus === TransactionStatus.FAILED) {
    return {
      titleText: "Failed to upload data",
      description: "Data submission failed in validation.",
      variant: "error",
      action: (
        <>
          <RoutingLink
            to={DataSetSubmissionDetailRoutePath.toLink({
              pathParams: {
                transactionId: transactionId ?? "",
              },
            })}
          >
            View Details
          </RoutingLink>
        </>
      ),
    };
  } else if (uploadError) {
    return {
      titleText: "Failed to upload data",
      description: "Data submission failed in ingestion.",
      variant: "error",
      action: null,
    };
  } else if (transactionStatus === TransactionStatus.PENDING) {
    return {
      titleText: "Uploading Data",
      description: "You may view the dataset details once completed",
      variant: "loading",
      action: null,
    };
  } else if (
    transactionStatus === TransactionStatus.COMPLETED_WITH_PARTIAL_SUCCESS
  ) {
    return {
      titleText: "Not all expected rows have been filled.",
      description: "Upload additional data or manually fill in records",
      variant: "warning",
      action: null,
    };
  } else {
    return null;
  }
};

const cx = classNames.bind(styles);
export const WorkflowTaskSubmitDataset = ({
  datasetId,
  workflowTask,
}: WorkflowSubmitDatasetProps) => {
  const { metadata } = workflowTask;
  //assume only one submission at a time for now
  const { dataset_submission_ids } =
    metadata as WorkflowSubmitDatasetTaskMetadata;
  const currentSubmissionId =
    dataset_submission_ids?.length > 0 ? dataset_submission_ids[0] : "";
  const { claims } = useAuthenticatedContext();

  const queryClient = useQueryClient();
  const [uploadError, setUploadError] = useState<boolean>(false);
  const { toast } = useToast();

  const { data: datasets, isLoading: areDatasetsLoading } = useListDatasets();
  const { data: templateBlob, isLoading: isTemplateBlobLoading } = useQuery(
    ["datasets", "template", datasetId ?? ""],
    () =>
      TemplateAdapter.dataset.getOne({
        datasetId: datasetId ?? "",
        companyId: claims.company.id!,
      }),
    {
      enabled: Boolean(datasetId) && Boolean(claims?.company?.id),
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  );
  const { data: transactionData, isLoading: isTransactionLoading } = useQuery(
    [Resources.TRANSACTION, currentSubmissionId],
    () =>
      TransactionAdapter.getOne({
        id: currentSubmissionId,
        meta: { history: false },
      }),
    {
      enabled: Boolean(currentSubmissionId),
      refetchInterval:
        workflowTask.status !== WorkflowStatus.COMPLETE ? 10000 : undefined,
    }
  );
  const transaction = transactionData?.data[0];
  const selectedDataset = useMemo(
    () => datasets?.data.find((d) => d.id === datasetId),
    [datasets]
  );

  const form = useForm<SubmitDatasetTaskFormType>();
  const files = form.watch("files");

  const handleDownloadTemplateClick = () => {
    if (!templateBlob) {
      toast.push({
        intent: "error",
        description: "Template not available!",
      });
      return;
    }

    const fileName =
      `${selectedDataset?.name ? `${snakeCase(selectedDataset?.name)}_` : ""}import_template`.toLowerCase();

    try {
      downloadBlob(fileName, templateBlob);
      toast.push({
        intent: "success",
        description: `Template ${fileName}.xlsx downloaded.`,
      });
    } catch (err) {
      toast.push({
        intent: "error",
        description: `Template failed to download.`,
      });
      ExceptionUtils.reportException(err, "error", {
        hint: "Failed to download import template for templated configuration",
      });
    }
  };

  const lastRegistrationRef = useRef<DatasetFileImportRegistrationType | null>(
    null
  );

  const datasetSubmissionMutation = useMutation({
    mutationFn: async (formValues: SubmitDatasetTaskFormType) => {
      await SubmitDatasetTaskFormSchema.safeParseAsync(formValues);
      const file = formValues.files.pop();
      if (!file || !datasetId || !claims) {
        throw new Error("No file to upload");
      }

      const { transactionId } = await uploadFileForDatasetImport({
        file,
        datasetId,
        companyId: claims.company?.id,
        previousRegistration: lastRegistrationRef.current,
        onRegistered: (registration) => {
          lastRegistrationRef.current = registration;
        },
      });

      const transaction = await TransactionAdapter.getOne({
        id: transactionId,
        meta: { history: false },
      });

      if (!transaction?.data.length) {
        throw new Error("No transactions found for initiated transaction");
      }

      await WorkflowTaskAdapter.updateOne({
        id: workflowTask.id,
        data: {
          dataset_submission_ids: [transactionId],
        },
        previousData: workflowTask,
      });

      lastRegistrationRef.current = null;
    },
    onSuccess: async () => {
      toast.push({
        intent: "success",
        description: `Data import started successfully.`,
      });
      queryClient.invalidateQueries({
        queryKey: [Resources.TRANSACTION],
      });
      await queryClient.invalidateQueries({
        queryKey: [
          Resources.WORKFLOW,
          "task",
          "getOne",
          { id: workflowTask.id },
        ],
      });
      form.reset();
    },
    onError: (err: Error) => {
      console.error(err);
      setUploadError(true);
    },
  });

  const clearForm = () => {
    //necessary for re-selecting a cleared file
    const inputRef = document.querySelector<HTMLInputElement>(
      'input[name="files"]'
    );
    if (inputRef) {
      inputRef.value = "";
    }

    form.reset();
  };
  const clearSubmissionMutation = useMutation({
    mutationFn: async () =>
      await WorkflowTaskAdapter.updateOne({
        id: workflowTask.id,
        data: {
          dataset_submission_ids: [],
        },
        previousData: workflowTask,
      }),
    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: [Resources.TRANSACTION],
      });
      await queryClient.invalidateQueries({
        queryKey: [
          Resources.WORKFLOW,
          "task",
          "getOne",
          { id: workflowTask.id },
        ],
      });
      clearForm();
    },
    onError: (err: Error) => {
      console.error(err);
      toast.push({
        intent: "error",
        description: `Failed to clear data`,
      });
    },
  });

  const bannerDetails = getBannerDetails(
    currentSubmissionId,
    transaction?.status,
    uploadError
  );

  return (
    <Form
      {...form}
      onSubmit={() => {
        form.handleSubmit((formData) => {
          setUploadError(false);
          datasetSubmissionMutation.mutate(formData);
        })();
      }}
    >
      {bannerDetails && workflowTask.status !== WorkflowTaskStatus.COMPLETE && (
        <div className={cx("banner-container")}>
          <Banner
            actionContent={bannerDetails.action ?? undefined}
            description={bannerDetails.description}
            isDismissable={false}
            titleText={bannerDetails.titleText}
            variant={bannerDetails.variant}
          />
        </div>
      )}
      <div className={cx("container")}>
        <div className={cx("title-container")}>
          <p className={cx("title")}>Data Submission</p>
          {!transaction && (
            <Button
              icon="download-simple"
              variant="outline"
              onClick={handleDownloadTemplateClick}
              isLoading={Boolean(
                areDatasetsLoading || (datasetId && isTemplateBlobLoading)
              )}
              disabled={!datasetId}
            >
              Download Template
            </Button>
          )}
          {!!transaction && (
            <div className={cx("button-container")}>
              {workflowTask.status !== WorkflowTaskStatus.COMPLETE && (
                <Button
                  variant="outline"
                  onClick={() => clearSubmissionMutation.mutate()}
                  isLoading={
                    Boolean(isTransactionLoading) ||
                    clearSubmissionMutation.isLoading
                  }
                  style={{ minHeight: 38 }}
                >
                  Re-Submit Data
                </Button>
              )}
              <RoutingLink
                to={DataSetSubmissionDetailRoutePath.toLink({
                  pathParams: {
                    transactionId: transaction.transaction_id ?? "",
                  },
                })}
              >
                <Button
                  icon="share"
                  variant="outline"
                  isLoading={Boolean(isTransactionLoading)}
                >
                  View
                </Button>
              </RoutingLink>
            </div>
          )}
        </div>
        {!transaction && (
          <>
            <FileInput
              name="files"
              placeholder="Drag .csv or .exe files here (max. 20 MB)"
              isRequired={true}
              rules={{
                required: "A file with data to import is required",
              }}
              acceptedFileTypes={acceptedMimeTypes}
              isDisabled={!datasetId || form.formState.isSubmitting}
              allowMultiple={false}
              buttonProps={{
                variant: "primary",
              }}
              className={cx("input")}
              isLoading={areDatasetsLoading}
            />
            {files?.length > 0 && (
              <div className={cx("button-container")}>
                <Button
                  key="clear"
                  onClick={() => clearForm()}
                >
                  Clear
                </Button>
                <Button
                  key="submit"
                  variant="primary"
                  type="submit"
                  isLoading={datasetSubmissionMutation.isLoading}
                >
                  Submit Data
                </Button>
              </div>
            )}
          </>
        )}
        {!!transaction && (
          <KeyValueList
            isLoading={areDatasetsLoading}
            variant="shaded"
            className={cx("key-value-list")}
            data={[
              {
                title: "Dataset Name",
                value: (
                  <TextDataDisplay
                    alignment="right"
                    value={selectedDataset?.name ?? "-"}
                  />
                ),
              },
              {
                title: "Import Type",
                value: (
                  <TextDataDisplay
                    alignment="right"
                    value={"Data Upload"}
                  />
                ),
              },
              {
                title: "File Name",
                value: (
                  <TextDataDisplay
                    alignment="right"
                    value={transaction?.original_file_name ?? "-"}
                  />
                ),
              },
              {
                title: "Total Rows",
                value: (
                  <NumberDataDisplay value={transaction?.total_count ?? 0} />
                ),
              },
              {
                title: "Successful Rows",
                value: (
                  <NumberDataDisplay
                    value={transaction?.successful_count ?? 0}
                  />
                ),
              },
            ]}
          />
        )}
      </div>
    </Form>
  );
};
